/** A single conversation row; owns its rename + kebab-menu state so edits re-render only this row (memoized). */

import { memo, useRef, useState } from "react"
import type { FocusEvent, KeyboardEvent } from "react"
import { styled } from "@mui/material/styles"
import { IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { DeleteOutlined, EditOutlined, MoreVert, PushPin, PushPinOutlined } from "@mui/icons-material"
import DeleteConfirmDialog from "connected-components/ai-teammate/components/delete-confirm-dialog"
import type { Thread } from "connected-components/ai-teammate/types/thread"
import { markdownToPlainText } from "./markdown-to-plain-text"

export interface ThreadRowCallbacks {
  onSelect: (threadId: string) => void
  /** Mutation handlers are absent on a select-only preview row (previewMode) — the kebab that fires them isn't rendered. */
  onRename?: (threadId: string, title: string) => void
  onPin?: (threadId: string) => void
  /** Fire-and-forget: the row goes now and a failure is reported by snackbar (PROD-452). */
  onDelete?: (threadId: string) => void
}

interface ThreadListItemProps {
  thread: Thread
  isActive: boolean
  canPinThreads?: boolean
  callbacks: ThreadRowCallbacks
  isMutating?: boolean
}

interface ThreadListItemPreviewProps {
  thread: Thread
  isActive: boolean
  onSelect: (threadId: string) => void
}

// alignSelf center: vertically center the pin against the row's title (the row is flex-start-aligned).
const PIN_GLYPH_SX = { minWidth: 0, mr: 1, alignSelf: "center", color: "primary.main" } satisfies SxProps<Theme>
const MENU_ICON_SX = { minWidth: 32 } satisfies SxProps<Theme>
const EDIT_FIELD_SX = { flex: 1 } satisfies SxProps<Theme>
// Match the title's inset (row button margin 0.5rem + MUI's 16px padding) so the text doesn't shift on rename.
const EDIT_ROW_SX = { pl: "calc(0.5rem + 16px)", pr: "0.5rem" } satisfies SxProps<Theme>
// Clamp to ~3 lines (legacy) rather than truncate to one.
const TITLE_CLAMP_SX = { display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden", overflowWrap: "anywhere" } satisfies SxProps<Theme>

// One style injection for every row (lists can be long).
const ThreadRow = styled(ListItem)(({ theme }) => ({
  "& .thread-kebab": {
    opacity: 0,
    transition: theme.transitions.create("opacity"),
  },
  "&:hover .thread-kebab, &:focus-within .thread-kebab, &.row-active .thread-kebab, &.menu-open .thread-kebab": {
    opacity: 1,
  },
}))

// Scheme-aware app CSS vars, not theme.palette.* (cssVariables freezes those to light). paddingRight reserves the kebab lane.
const ThreadRowButton = styled(ListItemButton)(({ theme }) => ({
  margin: "0.25rem 0.5rem",
  width: "calc(100% - 1rem)",
  borderRadius: "6px",
  paddingTop: "0.25rem",
  paddingBottom: "0.25rem",
  paddingRight: theme.spacing(5),
  alignItems: "flex-start",
  "&:hover": { backgroundColor: "var(--app-subtle-hover-surface)" },
  // Selected uses the primary tint (mirrors the active tab chip), not a neutral grey: a flat grey-lighter fill
  // sat only ~one shade off the neutral hover in dark mode (selected vs hover barely read apart). color-mix on
  // --app-primary is scheme-aware (the var shifts by mode) so selected stays obviously distinct in BOTH schemes.
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: "color-mix(in srgb, var(--app-primary) 12%, transparent)",
  },
}))

// Select-only preview row for the dock peek: same visuals (active tint, pin glyph, title clamp) minus every
// mutation overlay (kebab, rename, delete) — and no per-row edit/menu state to allocate (APP-3397).
function ThreadListItemPreviewBase({ thread, isActive, onSelect }: ThreadListItemPreviewProps) {
  const isPinnedGlyph = thread.pinned === true
  const displayTitleText = markdownToPlainText(thread.title)
  return (
    <ThreadRow disablePadding className={isActive ? "row-active" : undefined}>
      <ThreadRowButton selected={isActive} onClick={() => onSelect(thread.threadId)}>
        {isPinnedGlyph && (
          <ListItemIcon sx={PIN_GLYPH_SX}>
            <PushPin fontSize="inherit" />
          </ListItemIcon>
        )}
        <ListItemText primary={displayTitleText} slotProps={{ primary: { className: "thread-title", sx: TITLE_CLAMP_SX } }} />
      </ThreadRowButton>
    </ThreadRow>
  )
}

export const ThreadListItemPreview = memo(ThreadListItemPreviewBase)

function ThreadListItemBase({ thread, isActive, canPinThreads = false, callbacks, isMutating = false }: ThreadListItemProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [draft, setDraft] = useState(thread.title)
  // Set when Escape routes through blur, so the blur handler cancels instead of commits.
  const escapedRef = useRef(false)

  const openMenu = (anchor: HTMLElement) => setMenuAnchor(anchor)
  const closeMenu = () => setMenuAnchor(null)

  const startEditing = () => {
    closeMenu()
    setDraft(thread.title)
    setIsEditing(true)
  }

  const confirmDelete = () => {
    setIsConfirmingDelete(false)
    callbacks.onDelete?.(thread.threadId)
  }

  // Enter and Escape both blur; the blur handler is the single commit/cancel path.
  const handleEditKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.blur()
    } else if (event.key === "Escape") {
      event.preventDefault()
      escapedRef.current = true
      event.currentTarget.blur()
    }
  }

  const handleEditBlur = () => {
    const wasEscaped = escapedRef.current
    escapedRef.current = false
    setIsEditing(false)
    const next = draft.trim()
    if (wasEscaped || !next || next === thread.title) {
      setDraft(thread.title)
      return
    }
    callbacks.onRename?.(thread.threadId, next)
  }

  const isPinnedGlyph = thread.pinned === true
  const displayTitleText = markdownToPlainText(thread.title)

  if (isEditing) {
    return (
      <ListItem disablePadding sx={EDIT_ROW_SX}>
        <TextField
          size="small"
          variant="standard"
          margin="none"
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          sx={EDIT_FIELD_SX}
          slotProps={{
            // Handlers must attach to the <input> (not the InputBase root) so `currentTarget.blur()` blurs it.
            htmlInput: {
              "aria-label": "Rename conversation",
              onKeyDown: handleEditKeyDown,
              onBlur: handleEditBlur,
              onFocus: (event: FocusEvent<HTMLInputElement>) => event.currentTarget.select(),
            },
          }}
        />
      </ListItem>
    )
  }

  return (
    <ThreadRow
      disablePadding
      className={isActive ? "row-active" : menuAnchor ? "menu-open" : undefined}
      secondaryAction={
        <IconButton
          className="thread-kebab"
          edge="end"
          size="small"
          aria-label={`Conversation options for ${displayTitleText}`}
          data-testid={`chatbox-thread-menu-${thread.threadId}`}
          onClick={(event) => {
            event.stopPropagation()
            openMenu(event.currentTarget)
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      }
    >
      <ThreadRowButton selected={isActive} onClick={() => callbacks.onSelect(thread.threadId)}>
        {isPinnedGlyph && (
          <ListItemIcon sx={PIN_GLYPH_SX}>
            <PushPin fontSize="inherit" />
          </ListItemIcon>
        )}
        <ListItemText primary={displayTitleText} slotProps={{ primary: { className: "thread-title", sx: TITLE_CLAMP_SX } }} />
      </ThreadRowButton>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem disabled={isMutating} onClick={startEditing}>
          <ListItemIcon sx={MENU_ICON_SX}>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          Rename
        </MenuItem>
        {canPinThreads && (
          <MenuItem
            disabled={isMutating}
            onClick={() => {
              closeMenu()
              callbacks.onPin?.(thread.threadId)
            }}
          >
            <ListItemIcon sx={MENU_ICON_SX}>
              {isPinnedGlyph ? <PushPinOutlined fontSize="small" /> : <PushPin fontSize="small" />}
            </ListItemIcon>
            {isPinnedGlyph ? "Unpin" : "Pin"}
          </MenuItem>
        )}
        <MenuItem
          disabled={isMutating}
          onClick={() => {
            closeMenu()
            setIsConfirmingDelete(true)
          }}
        >
          <ListItemIcon sx={MENU_ICON_SX}>
            <DeleteOutlined fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <DeleteConfirmDialog
        open={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        onConfirm={confirmDelete}
      />
    </ThreadRow>
  )
}

export const ThreadListItem = memo(ThreadListItemBase)
